import axios from "axios";
// Set your local IP address for development/
//const baseURL = "http://ec2-18-220-184-52.us-east-2.compute.amazonaws.com:3000/api";
//const baseURL = "http://localhost:3000/api";
const baseURL = "https://mysway.app/api";

const apiClient = axios.create({
  baseURL: baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to dynamically set the Authorization header
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    console.log('🔐 [API Client] Request to:', config.url);
    console.log('🔐 [API Client] Token present:', token ? 'Yes' : 'No');
    console.log('🔐 [API Client] Token value:', token);
    console.log('🔐 [API Client] Request method:', config.method);
    console.log('🔐 [API Client] Request headers before:', config.headers);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('🔐 [API Client] Authorization header set');
    } else {
      console.log('⚠️ [API Client] No token found - request will be unauthenticated');
    }
    
    // Add cache-busting headers to prevent browser caching issues
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';
    
    console.log('🔐 [API Client] Request headers after:', config.headers);
    return config;
  },
  (error) => {
    console.error('❌ [API Client] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to debug responses
apiClient.interceptors.response.use(
  (response) => {
    console.log('✅ [API Client] Response received:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('❌ [API Client] Response error:', error.response?.status, error.config?.url);
    console.error('❌ [API Client] Error data:', error.response?.data);
    return Promise.reject(error);
  }
);

export default apiClient;

// Helper function to sanitize filenames
const sanitizeFilename = (filename) => {
  if (!filename) return 'unnamed-file';
  
  // Remove or replace problematic characters
  let sanitized = filename
    .replace(/[^\w\s.-]/g, '') // Remove special chars and emojis
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores
    .toLowerCase(); // Convert to lowercase
  
  // Ensure we have a valid filename
  if (!sanitized || sanitized.length < 3) {
    const timestamp = Date.now();
    const extension = filename.split('.').pop() || 'file';
    sanitized = `upload_${timestamp}.${extension}`;
  }
  
  // Limit length to reasonable size
  if (sanitized.length > 100) {
    const extension = sanitized.split('.').pop();
    const baseName = sanitized.substring(0, 90 - extension.length);
    sanitized = `${baseName}.${extension}`;
  }
  
  console.log('🔧 [Upload] Original filename:', filename);
  console.log('🔧 [Upload] Sanitized filename:', sanitized);
  
  return sanitized;
};

export const uploadFile = async (file) => {
  // Sanitize filename to avoid server issues with special characters
  const sanitizedFile = new File([file], sanitizeFilename(file.name), {
    type: file.type,
    lastModified: file.lastModified,
  });
  
  const formData = new FormData();
  formData.append("file", sanitizedFile);
  
  try {
    console.log("📁 [Upload] Uploading file:", sanitizedFile.name || sanitizedFile);
    console.log("📁 [Upload] File size:", (sanitizedFile.size / 1024 / 1024).toFixed(2), "MB");
    console.log("📁 [Upload] File type:", sanitizedFile.type);
    
    // Get the current token for authorization
    const token = localStorage.getItem("token");
    
    const response = await axios.post(  
      baseURL + "/uploads/many",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "Authorization": token ? `Bearer ${token}` : undefined,
        },
      },
    );
    
    console.log("📁 [Upload] Raw upload response:", response);
    console.log("📁 [Upload] Response data:", response.data);
    console.log("📁 [Upload] Response data structure:", JSON.stringify(response.data, null, 2));

    // Let's check different possible response structures
    if (response.data) {
      if (response.data.data && Array.isArray(response.data.data) && response.data.data[0] && response.data.data[0].path) {
        // Original expected format
        const filePath = response.data.data[0].path;
        const fileBase = baseURL.replace(/\/api$/, "");
        const fileUrl = fileBase + "/" + filePath;
        console.log("📁 [Upload] Using original format - File URL:", fileUrl);
        return fileUrl;
      } else if (response.data.data && response.data.data.url) {
        // Alternative format with direct URL
        console.log("📁 [Upload] Using direct URL format:", response.data.data.url);
        return response.data.data.url;
      } else if (response.data.url) {
        // Direct URL in response.data
        console.log("📁 [Upload] Using response.data.url format:", response.data.url);
        return response.data.url;
      } else if (response.data.data && response.data.data[0] && response.data.data[0].url) {
        // Array with URL - check if it's a video with metadata
        const uploadData = response.data.data[0];
        console.log("📁 [Upload] Using array URL format:", uploadData.url);

        // If video has HLS metadata, return the full object
        if (uploadData.videoHLS || uploadData.videoQualities) {
          console.log("📁 [Upload] Detected video with HLS metadata, returning full object");
          return uploadData;
        }

        // Otherwise just return the URL (for images)
        return uploadData.url;
      } else if (response.data.data && typeof response.data.data === 'string') {
        // Data as string (maybe path)
        const fileBase = baseURL.replace(/\/api$/, "");
        const fileUrl = fileBase + "/" + response.data.data;
        console.log("📁 [Upload] Using string data format - File URL:", fileUrl);
        return fileUrl;
      }
    }
    
    console.error("📁 [Upload] Unexpected upload response format:", response.data);
    console.error("📁 [Upload] Available keys in response.data:", Object.keys(response.data || {}));
    throw new Error("Unexpected upload response format - check console for details");
  } catch (error) {
    console.error("📁 [Upload] Error uploading file:", error);
    console.error("📁 [Upload] Error response status:", error.response?.status);
    console.error("📁 [Upload] Error response data:", error.response?.data);
    console.error("📁 [Upload] Error response headers:", error.response?.headers);
    
    let errorMessage = 'Upload failed';
    if (error.response?.status === 500) {
      errorMessage = 'Server error (500) - possibly file size, format, or server configuration issue';
    } else if (error.response?.status === 413) {
      errorMessage = 'File too large (413) - reduce file size';
    } else if (error.response?.status === 415) {
      errorMessage = 'Unsupported media type (415) - check file format';
    } else if (error.response?.status === 408) {
      errorMessage = 'Request timeout (408) - file upload took too long';
    } else {
      errorMessage = error.response?.data?.message || error.message || 'Upload failed';
    }
    
    throw new Error(`Error uploading file: ${errorMessage}`);
  }
};

export const uploadBase = async (file) => {
  try {
    const response = await axios.post(
      baseURL + "/uploads/base",
      { file: "data:image/png;base64,\n" + file },
    );
    return response.data.data.url;
  } catch (error) {
    throw new Error("error uploading file");
  }
};

export const uploadmusictypeBase = async (file, fileType, musicGenreName) => {
  try {
    if (!musicGenreName) {
      throw new Error('Music genre name is required');
    }

    let base64Data;
    if (fileType === 'image') {
      base64Data = `data:image/png;base64,${file}`; 
    } else if (fileType === 'audio') {
      base64Data = `data:audio/mp3;base64,${file}`; 
    } else {
      throw new Error('Unsupported file type');
    }
    const response = await axios.post(
      baseURL + "/uploads/musictypeBase",
      { file: base64Data, musicGenreName },  
    );

    return response.data.data.url;
  } catch (error) {
    throw new Error("Error uploading file: " + error.message);
  }
};

export const scrapeEvent = async (url) => {
  try {
    const response = await apiClient.post("/scraping/scrape", { url });
    return response.data;
  } catch (error) {
    throw new Error("Error scraping event: " + error.message);
  }
};

export const scrapeArtist = async (url) => {
  try {
    const response = await apiClient.post("/scraping/scrape-artist", { url });
    return response.data;
  } catch (error) {
    throw new Error("Error scraping artist: " + error.message);
  }
};

export const uploadFromUrl = async (url) => {
  try {
    const response = await axios.post(
      baseURL + "/uploads/from-url",
      { url },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data.data.url;
  } catch (error) {
    console.error("Error uploading from URL:", error);
    throw new Error("Error uploading from URL");
  }
};

